/**
 * Invoicing labels, tag colors, and antd Select option arrays.
 * Enum values are the canonical BE strings (see types/api/invoicing).
 */
import config from 'config';
import {
  InvoiceDocumentType,
  InvoiceLanguage,
  InvoicePaymentMethod,
  InvoiceServiceType,
  InvoiceStatus,
  InvoiceTaxMode
} from 'types/api/invoicing';

/**
 * On-domain patient pay page URL for an invoice — the shareable "pay link".
 * Payment moved on-domain, so this is built from publicId (the old
 * stripePaymentLinkUrl is deprecated). `config.website_url` is the patient site.
 */
export const buildInvoicePayUrl = (
  publicId: string,
  language?: InvoiceLanguage | string | null
): string =>
  `${config.website_url}/${language || InvoiceLanguage.En}/pay/${publicId}`;

/**
 * The pay link to hand to a payer — ALWAYS our own page, never a Stripe URL.
 *
 * Every Stripe pay URL expires: a hosted invoice page 30 days after the due
 * date (120 at the outside), and a Checkout session within 24 hours. Handing
 * one to an admin to copy into an email is how a payer ends up on a dead
 * link days later. Our page never expires and forwards to a freshly minted
 * session each time it is opened, so it is the only safe thing to copy.
 *
 * `stripeHostedInvoiceUrl` is deliberately ignored here. It is still read
 * elsewhere for the two legacy invoices that hold one, but it must not be
 * the link we give out.
 */
export const invoicePayUrl = (invoice: {
  publicId: string;
  language?: InvoiceLanguage | string | null;
}): string => buildInvoicePayUrl(invoice.publicId, invoice.language);

export const INVOICE_STATUS_LABELS: Record<InvoiceStatus, string> = {
  [InvoiceStatus.Draft]: 'Draft',
  [InvoiceStatus.Issued]: 'Issued',
  [InvoiceStatus.Paid]: 'Paid',
  [InvoiceStatus.Cancelled]: 'Cancelled'
};

// antd Tag colors
export const INVOICE_STATUS_COLORS: Record<InvoiceStatus, string> = {
  [InvoiceStatus.Draft]: 'default',
  [InvoiceStatus.Issued]: 'blue',
  [InvoiceStatus.Paid]: 'green',
  [InvoiceStatus.Cancelled]: 'red'
};

export const INVOICE_DOCUMENT_TYPE_LABELS: Record<InvoiceDocumentType, string> =
  {
    [InvoiceDocumentType.Invoice]: 'Invoice',
    [InvoiceDocumentType.CreditNote]: 'Credit note'
  };

export const INVOICE_PAYMENT_METHOD_LABELS: Record<
  InvoicePaymentMethod,
  string
> = {
  [InvoicePaymentMethod.Stripe]: 'Stripe',
  [InvoicePaymentMethod.BankTransfer]: 'Bank transfer'
};

export const INVOICE_TAX_MODE_LABELS: Record<InvoiceTaxMode, string> = {
  [InvoiceTaxMode.None]: 'No tax',
  [InvoiceTaxMode.PerLine]: 'Per line',
  [InvoiceTaxMode.BaseOverride]: 'Base override'
};

export const INVOICE_SERVICE_TYPE_LABELS: Record<InvoiceServiceType, string> = {
  [InvoiceServiceType.Consultation]: 'Consultation',
  [InvoiceServiceType.SecondOpinion]: 'Second opinion',
  [InvoiceServiceType.Translation]: 'Translation',
  [InvoiceServiceType.Teleradiology]: 'Teleradiology',
  [InvoiceServiceType.Radiology]: 'Radiology',
  [InvoiceServiceType.Other]: 'Other'
};

export const INVOICE_LANGUAGE_LABELS: Record<InvoiceLanguage, string> = {
  [InvoiceLanguage.En]: 'English',
  [InvoiceLanguage.De]: 'German'
};

const toOptions = <T extends string>(labels: Record<T, string>) =>
  (Object.keys(labels) as T[]).map(value => ({ value, label: labels[value] }));

export const INVOICE_STATUS_OPTIONS = toOptions(INVOICE_STATUS_LABELS);
export const INVOICE_PAYMENT_METHOD_OPTIONS = toOptions(
  INVOICE_PAYMENT_METHOD_LABELS
);
export const INVOICE_TAX_MODE_OPTIONS = toOptions(INVOICE_TAX_MODE_LABELS);
export const INVOICE_SERVICE_TYPE_OPTIONS = toOptions(
  INVOICE_SERVICE_TYPE_LABELS
);
export const INVOICE_LANGUAGE_OPTIONS = toOptions(INVOICE_LANGUAGE_LABELS);
